import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trash2, Eye, DollarSign, Calendar, User, CreditCard, Plus } from 'lucide-react';
import { splitPayTransactionApi } from '@/lib/api/splitPayTransactionApi';
import { SplitPayTransactionSummary } from '@/lib/api/types';
import type { SplitPayTransactionFilters } from '@/components/SplitPayTransactionFilters';

interface SplitPayTransactionCardProps {
  transaction: SplitPayTransactionSummary;
  onDelete: (transactionId: string) => void;
  onViewDetails: (transactionId: string) => void;
}

const SplitPayTransactionCard: React.FC<SplitPayTransactionCardProps> = ({
  transaction,
  onDelete,
  onViewDetails,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount: number, currencyCode: string) => {
    // Convert from minor units (cents) to decimal (dollars)
    const amountInDecimal = amount / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amountInDecimal);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg font-semibold">
            Transaction {transaction.TransactionId.slice(-8)}
          </CardTitle>
          <div className="flex gap-2">
            <Badge className={getStatusColor(transaction.Status)}>
              {transaction.Status}
            </Badge>
            {transaction.IsReversed && (
              <Badge className="bg-orange-100 text-orange-800">
                Reversed
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <DollarSign className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">Amount:</span>
            <span className="font-medium">
              {formatAmount(transaction.Amount, transaction.CurrencyCode)}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <User className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">Payer:</span>
            <span className="font-medium">{transaction.PayerAccountId}</span>
          </div>
          <div className="flex items-center space-x-2">
            <CreditCard className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">Payment Method:</span>
            <span className="font-medium">{transaction.PaymentMethodId}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">Created:</span>
            <span className="font-medium">{formatDate(transaction.CreatedDate)}</span>
          </div>
        </div>

        {transaction.Invoice && (
          <div className="mb-4">
            <span className="text-sm text-gray-600">Invoice:</span>
            <span className="ml-2 font-medium">{transaction.Invoice}</span>
          </div>
        )}

        {(transaction.Comment1 || transaction.Comment2) && (
          <div className="mb-4">
            {transaction.Comment1 && (
              <div>
                <span className="text-sm text-gray-600">Comment 1:</span>
                <span className="ml-2">{transaction.Comment1}</span>
              </div>
            )}
            {transaction.Comment2 && (
              <div>
                <span className="text-sm text-gray-600">Comment 2:</span>
                <span className="ml-2">{transaction.Comment2}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(transaction.TransactionId)}
          >
            <Eye className="h-4 w-4 mr-1" />
            View Details
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDelete(transaction.TransactionId)}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

interface SplitPayTransactionListProps {
  onCreateTransaction: () => void;
  onViewTransactionDetails: (transactionId: string) => void;
  filters?: SplitPayTransactionFilters;
  limit?: number;
  offset?: number;
}

export const SplitPayTransactionList: React.FC<SplitPayTransactionListProps> = ({
  onCreateTransaction,
  onViewTransactionDetails,
  filters = {},
  limit = 50,
  offset = 0,
}) => {
  const queryClient = useQueryClient();

  // Fetch transactions with filters
  const { data: transactionsData, isLoading, error } = useQuery({
    queryKey: ['splitPayTransactions', filters, limit, offset],
    queryFn: () => splitPayTransactionApi.getAllSplitPayTransactions(
      limit,
      offset,
      filters.payerId,
      filters.paymentMethodId,
      filters.status === '' ? undefined : filters.status,
      filters.startDate,
      filters.endDate,
      filters.minAmount,
      filters.maxAmount,
      filters.isReversed,
      'json'
    ),
  });

  const deleteTransactionMutation = useMutation({
    mutationFn: splitPayTransactionApi.deleteSplitPayTransaction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['splitPayTransactions'] });
    },
  });

  const clearAllTransactionsMutation = useMutation({
    mutationFn: splitPayTransactionApi.clearAllSplitPayTransactions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['splitPayTransactions'] });
    },
  });

  const handleDeleteTransaction = (transactionId: string) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      deleteTransactionMutation.mutate(transactionId);
    }
  };

  const handleClearAllTransactions = () => {
    if (window.confirm('Are you sure you want to delete ALL transactions? This action cannot be undone.')) {
      clearAllTransactionsMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading transactions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-red-600">
          Error loading transactions: {(error as Error).message}
        </div>
      </div>
    );
  }

  // Handle the response type (can be string for CSV or object for JSON)
  const transactions = (typeof transactionsData === 'object' && transactionsData?.SplitPayTransactions) || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Transactions</h2>
          <p className="text-gray-600">
            Total: {(typeof transactionsData === 'object' && transactionsData?.TotalCount) || 0} transactions
          </p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={onCreateTransaction}>
            <Plus className="h-4 w-4 mr-2" />
            Create Transaction
          </Button>
          {transactions.length > 0 && (
            <Button
              variant="destructive"
              onClick={handleClearAllTransactions}
              disabled={clearAllTransactionsMutation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {clearAllTransactionsMutation.isPending ? 'Clearing...' : 'Clear All'}
            </Button>
          )}
        </div>
      </div>

      {transactions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <DollarSign className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Transactions</h3>
            <p className="text-gray-600 text-center">
              There are currently no transactions in the system.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div>
          {transactions.map((transaction: SplitPayTransactionSummary) => (
            <SplitPayTransactionCard
              key={transaction.TransactionId}
              transaction={transaction}
              onDelete={handleDeleteTransaction}
              onViewDetails={onViewTransactionDetails}
            />
          ))}
        </div>
      )}
    </div>
  );
};
